import { useParams } from 'react-router-dom';
import { useAcademyHost } from '../contexts/AcademyHostContext';
import { useOrgHost } from '@/contexts/OrgHostContext';
import { useOrgPublicProfile } from '@/contexts/OrgPublicProfileContext';
import { useGuardianAcademyStore } from '../store/guardianAcademy';
import { useCoachAcademyStore } from '../store/coachAcademy';
import { useOrgSlug } from './useOrgSlug';
import { usePublicOrgProfile } from '@/queries/publicOrgProfile';

function useOrgProfileAcademySlug(): {
  academySlug?: string;
  isResolving: boolean;
} {
  const { academySlug: academySlugParam } = useParams<{ academySlug?: string }>();
  const { pathSlug, host } = useAcademyHost();
  const { academySlug: orgHostAcademySlug, loading: orgHostLoading } = useOrgHost();
  const orgSlug = useOrgSlug();
  const { profile: contextProfile, isLoading: contextLoading, orgSlug: contextOrgSlug } =
    useOrgPublicProfile();

  const needsOrgProfile =
    !academySlugParam &&
    !orgHostAcademySlug &&
    !(host.mode === 'subdomain' && pathSlug) &&
    !!orgSlug &&
    !contextProfile &&
    !(contextLoading && contextOrgSlug);

  const { profile, isLoading: profileLoading } = usePublicOrgProfile({
    slug: orgSlug,
    subdomainLabel: host.mode === 'subdomain' ? host.subdomainLabel : undefined,
    hostLoading: host.mode === 'subdomain' && orgHostLoading,
    enabled: needsOrgProfile,
  });

  if (contextProfile) {
    return {
      academySlug:
        contextProfile.academySlug ??
        (contextProfile.modules.hasAcademy && orgSlug ? orgSlug : undefined),
      isResolving: false,
    };
  }

  if (contextLoading && contextOrgSlug) {
    return { academySlug: undefined, isResolving: true };
  }

  if (!needsOrgProfile) {
    return { academySlug: undefined, isResolving: false };
  }

  if (profileLoading && !profile) {
    return { academySlug: undefined, isResolving: true };
  }

  const academySlug =
    profile?.academySlug ??
    (profile?.modules.hasAcademy && orgSlug ? orgSlug : undefined);

  return { academySlug, isResolving: false };
}

/**
 * Resolved path slug for academy-scoped API calls and path-mode links.
 * On subdomains, uses the loaded academy's slug (not the hostname label).
 * On unified org routes (`/club-academy/:orgSlug`), resolves from the public org profile.
 */
export function useAcademySlug(): string | undefined {
  const { academySlug: academySlugParam } = useParams<{ academySlug?: string }>();
  const { pathSlug, host } = useAcademyHost();
  const { academySlug: orgHostAcademySlug } = useOrgHost();
  const { academySlug: orgProfileAcademySlug } = useOrgProfileAcademySlug();
  const guardianSlug = useGuardianAcademyStore((s) => s.academySlug);
  const coachSlug = useCoachAcademyStore((s) => s.academySlug);

  if (academySlugParam) return academySlugParam;
  if (host.mode === 'subdomain' && pathSlug) return pathSlug;
  if (orgHostAcademySlug) return orgHostAcademySlug;
  if (orgProfileAcademySlug) return orgProfileAcademySlug;

  return guardianSlug ?? coachSlug ?? undefined;
}

/** True while unified org profile is loading to resolve the academy slug. */
export function useAcademySlugResolving(): boolean {
  return useOrgProfileAcademySlug().isResolving;
}
